// docs/114 MT9 — the interactive ground on the board: what lies on each square (a splotch per
// surface on `low`, shimmering, bubbling, sparking or burning), what hangs over it (drifting puffs
// on `high`), the things on the field (on `props`), and the one-shot flourish of a reaction firing.
// `set` diffs against what is drawn, so a mid-playback redraw only touches the squares and things
// that changed. Particles come from a handful of shared emitters whose zones are the squares that
// need them, never one emitter per square.
import Phaser from 'phaser';
import type { CloudName, MatterPatch, ReactLook, SurfaceName, ThingView, Tile } from '../../core/sim/types';
import { ART, MTEX, SPILL, ensureMatterTextures, markKey, surfaceKey } from './textures';
import { ALWAYS_ANIMATED, FALLBACK, LIGHTS, THING_LOOK } from './things';
import { anchorPx, footprint, standDepth, thingCovers, thingShape } from '../../core/matter/shapes';
import { OBJECT_ATLAS, objectTable, objectVariant } from './objectArt';

type Layer = Phaser.GameObjects.Layer;
type Emitter = Phaser.GameObjects.Particles.ParticleEmitter;

const key = (t: Tile) => `${t.x},${t.y}`;
const hash = (t: Tile, salt = 0) => (((t.x * 73856093) ^ (t.y * 19349663) ^ (salt * 83492791)) >>> 0);

/** Surfaces that shimmer as liquids do. */
const LIQUID: ReadonlySet<SurfaceName> = new Set(['Water', 'Oil', 'Grease', 'Acid', 'Blood', 'Honey', 'Spirits', 'HolyWater', 'Venom', 'Slime', 'Brine', 'Blight', 'Tar', 'Sap', 'Glass', 'Ice']);
/** Surfaces that bubble and give off a faint wisp: "smokes and sizzles lightly". */
const FIZZ: ReadonlySet<SurfaceName> = new Set(['Acid', 'Venom', 'Blight', 'Slime']);
/** Surfaces whose flames burn tall. */
const TALL_FLAME: ReadonlySet<SurfaceName> = new Set(['Oil', 'Tar', 'Grease', 'Spirits']);

/** How each cloud looks: tint, opacity, and the motes that drift in it. */
const CLOUD_ART: Record<CloudName, { tint: number; alpha: number; motes?: { tint: number[]; tex: string } }> = {
  Steam: { tint: 0xf4f6f8, alpha: 0.62 },
  Smoke: { tint: 0x3a3a3e, alpha: 0.72 },
  Fumes: { tint: 0x8ab83a, alpha: 0.45 },
  Marsh: { tint: 0xc8d890, alpha: 0.32 },
  Dust: { tint: 0xd8c8a0, alpha: 0.66 },
  Spores: { tint: 0x8a8a3a, alpha: 0.6, motes: { tint: [0xd8d870, 0xa8a840], tex: MTEX.dot } },
  Mist: { tint: 0xdfe8f4, alpha: 0.7 },
  Incense: { tint: 0xfff0c8, alpha: 0.42, motes: { tint: [0xffe08a, 0xffffff], tex: MTEX.dot } },
  Frost: { tint: 0xcff0ff, alpha: 0.45, motes: { tint: [0xffffff, 0xcff0ff], tex: MTEX.spark } },
  Swarm: { tint: 0x2a2410, alpha: 0.12, motes: { tint: [0x1a1408, 0x3a2a10, 0xc89a2a], tex: MTEX.dot } },
  Pollen: { tint: 0xf2e27a, alpha: 0.3, motes: { tint: [0xffe84a, 0xfff2a0], tex: MTEX.dot } },
  Ash: { tint: 0x5a5856, alpha: 0.66, motes: { tint: [0x8a8884, 0x3a3836], tex: MTEX.dot } },
  Cinders: { tint: 0x5a3020, alpha: 0.25, motes: { tint: [0xffa040, 0xff6a2a, 0xffe08a], tex: MTEX.dot } },
  // docs/189 EO5: hens loosed from a coop
  Feathers: { tint: 0xf4ecd8, alpha: 0.55, motes: { tint: [0xffffff, 0xf2e8d0, 0xa8784a, 0x8a5a30], tex: MTEX.bit } },
};

type RGB = readonly [number, number, number];
/** docs/189: what a thing drawn among the units reads of the light map and the fog (as the wildlife does). */
export interface ThingHooks {
  units: Layer;
  /** the light map at a board point in squares; undefined when lighting is off */
  light?(cx: number, cy: number): RGB | undefined;
  /** 0 never seen, 1 remembered, 2 in sight */
  fog?(x: number, y: number): 0 | 1 | 2;
  /** the fog's memory veil, as a multiply */
  memory?(): RGB | undefined;
}

const hexOf = (r: number, g: number, b: number) => (Math.round(Math.min(1, Math.max(0, r)) * 255) << 16) | (Math.round(Math.min(1, Math.max(0, g)) * 255) << 8) | Math.round(Math.min(1, Math.max(0, b)) * 255);

interface DrawnSurface { img: Phaser.GameObjects.Image; surface: SurfaceName; phase: number; glow?: Phaser.GameObjects.Image; spark?: Phaser.GameObjects.Image }
/** A cloud's puff and its drift: out by (dx, dy) and back over `period` ms, its alpha `a0` → `a1` and back. */
interface CloudPuff { img: Phaser.GameObjects.Image; x: number; y: number; dx: number; dy: number; a0: number; a1: number; period: number; t0?: number }
interface DrawnCloud { puffs: CloudPuff[]; cloud: CloudName }
interface DrawnThing {
  view: ThingView; objs: Phaser.GameObjects.GameObject[]; img?: Phaser.GameObjects.Image; frames?: [string, string]; atlas?: string; glow?: Phaser.GameObjects.Image;
  /** docs/189: drawn from the objects atlas among the units: lit and fogged by hand (`shade`) */
  among?: boolean;
}

export class MatterLayer {
  private readonly surfaces = new Map<string, DrawnSurface>();
  private readonly clouds = new Map<string, DrawnCloud>();
  private readonly things = new Map<number, DrawnThing>();
  /** Things hidden under a rolling trap's own drawing until the next board (`hideThingsOn`). */
  private readonly hidden = new Set<number>();
  private patches: readonly MatterPatch[] = [];
  private emitters: { fire?: Emitter; fizz?: Emitter; wisp?: Emitter; ember?: Emitter; charge?: Emitter; motes: Map<CloudName, Emitter> } = { motes: new Map() };
  /** Squares each shared emitter draws from (the closures read these live). */
  private zones: { fire: Tile[]; tall: Tile[]; fizz: Tile[]; ember: Tile[]; charge: Tile[]; motes: Map<CloudName, Tile[]> } = { fire: [], tall: [], fizz: [], ember: [], charge: [], motes: new Map() };
  private oneShots = new Set<Phaser.GameObjects.GameObject>();
  private readonly still: boolean;
  private frameFlip = false;
  private flipTimer?: Phaser.Time.TimerEvent;
  private readonly onUpdate: (time: number) => void;
  private destroyed = false;
  private readonly hooks?: ThingHooks;
  private shadeAt = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly layers: { low: Layer; props: Layer; high: Layer; effects: Layer },
    private readonly cell: number,
    opts?: { reduceMotion?: boolean; things?: ThingHooks },
  ) {
    this.hooks = opts?.things;
    this.still = !!opts?.reduceMotion;
    ensureMatterTextures(scene);
    this.onUpdate = (time: number) => this.tick(time);
    if (!this.still) {
      scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
      this.flipTimer = scene.time.addEvent({ delay: 280, loop: true, callback: () => this.flip() });
    }
  }

  // ------------------------------------------------------------------------------------------
  // the board's matter and things
  // ------------------------------------------------------------------------------------------

  set(matter: readonly MatterPatch[] | undefined, things: readonly ThingView[] | undefined): void {
    if (this.destroyed) return;
    this.patches = matter ?? [];
    const want = new Map(this.patches.map((m) => [key(m.tile), m]));
    // surfaces
    for (const [k, d] of this.surfaces) {
      const m = want.get(k);
      if (!m?.surface || m.surface !== d.surface) { this.dropSurface(k); continue; }
    }
    for (const [k, m] of want) {
      const d = this.surfaces.get(k);
      if (m.surface && !d) this.addSurface(k, m.tile, m.surface);
      const now = this.surfaces.get(k);
      if (now) this.dress(now, m);
    }
    // clouds
    for (const [k, d] of this.clouds) {
      const m = want.get(k);
      if (!m?.cloud || m.cloud !== d.cloud) this.dropCloud(k);
    }
    for (const [k, m] of want) if (m.cloud && !this.clouds.has(k)) this.addCloud(k, m.tile, m.cloud);
    // things
    const live = new Map((things ?? []).filter((t) => !t.spent).map((t) => [t.id, t]));
    for (const [id, d] of this.things) {
      const t = live.get(id);
      if (!t || t.kind !== d.view.kind || t.lit !== d.view.lit || t.tile.x !== d.view.tile.x || t.tile.y !== d.view.tile.y || (t.burn > 0) !== (d.view.burn > 0)) this.dropThing(id);
      else d.view = t;
    }
    for (const [id, t] of live) if (!this.things.has(id)) this.addThing(t);
    for (const id of this.hidden) for (const o of this.things.get(id)?.objs ?? []) (o as unknown as { setVisible?(v: boolean): unknown }).setVisible?.(true);
    this.hidden.clear();
    this.rezone();
    this.shadeAll();
  }

  /**
   * docs/140 DT7: hide whatever thing stands on `tiles` while a trap's roller (drawn by the trap's
   * effects) rolls away from there; the next board shows it again where it came to rest.
   */
  hideThingsOn(tiles: readonly Tile[]): void {
    for (const d of this.things.values()) {
      if (!tiles.some((t) => thingCovers(d.view, t))) continue;
      for (const o of d.objs) (o as unknown as { setVisible?(v: boolean): unknown }).setVisible?.(false);
      this.hidden.add(d.view.id);
    }
  }

  thingAt(t: Tile): ThingView | undefined {
    for (const d of this.things.values()) if (thingCovers(d.view, t)) return d.view;
    return undefined;
  }

  lights(): { tile: Tile; color: number; radius: number; flicker: boolean }[] {
    const out: { tile: Tile; color: number; radius: number; flicker: boolean }[] = [];
    for (const m of this.patches) {
      if (m.fire > 0) out.push({ tile: m.tile, color: 0xff8a30, radius: m.surface && TALL_FLAME.has(m.surface) ? 3 : 2, flicker: true });
      else if (m.surface === 'Embers') out.push({ tile: m.tile, color: 0xff6a2a, radius: 1, flicker: true });
      else if (m.charge > 0) out.push({ tile: m.tile, color: 0x9fd8ff, radius: 2, flicker: true });
    }
    for (const d of this.things.values()) {
      const t = d.view;
      if (!t.lit) continue;
      const look = THING_LOOK[t.kind];
      // a thing of many squares shines from the middle of its footprint's front row
      const sh = thingShape(t.kind);
      const tile = { x: t.tile.x + Math.floor((sh.w - 1) / 2), y: t.tile.y + sh.h - 1 };
      if (LIGHTS.includes(t.kind) && look.light) out.push({ tile, color: look.light.color, radius: look.light.radius, flicker: true });
      else if (t.burn > 0) out.push({ tile, color: 0xff8a30, radius: 3 + Math.max(0, Math.min(sh.w, sh.h) - 1), flicker: true });
    }
    return out;
  }

  // ------------------------------------------------------------------------------------------
  // surfaces
  // ------------------------------------------------------------------------------------------

  private centre(t: Tile): { x: number; y: number } {
    return { x: t.x * this.cell + this.cell / 2, y: t.y * this.cell + this.cell / 2 };
  }

  private addSurface(k: string, t: Tile, s: SurfaceName): void {
    const c = this.centre(t);
    const v = hash(t) % 3;
    const img = this.scene.add.image(c.x, c.y, surfaceKey(s, v));
    const size = (this.cell * (ART + SPILL * 2)) / ART;
    img.setDisplaySize(size, size);
    // a quarter turn by square, so a pool of the same stuff never tiles in a grid
    img.setAngle((hash(t, 7) % 4) * 90);
    this.layers.low.add(img);
    this.surfaces.set(k, { img, surface: s, phase: (hash(t, 3) % 1000) / 1000 * Math.PI * 2 });
  }

  /** A surface's fire glow and charge flicker, as its patch says. */
  private dress(d: DrawnSurface, m: MatterPatch): void {
    const c = this.centre(m.tile);
    if (m.fire > 0 && !d.glow) {
      d.glow = this.scene.add.image(c.x, c.y, MTEX.glow).setTint(0xff7a2a).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.55);
      d.glow.setDisplaySize(this.cell * 1.6, this.cell * 1.6);
      this.layers.low.add(d.glow);
      // what burns darkens under its flames
      d.img.setTint(0x8a6a5a);
    } else if (m.fire === 0 && d.glow) {
      d.glow.destroy(); d.glow = undefined; d.img.clearTint();
    }
    if (m.charge > 0 && !d.spark) {
      d.spark = this.scene.add.image(c.x, c.y, MTEX.glow).setTint(0xbfe8ff).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.6);
      d.spark.setDisplaySize(this.cell * 1.3, this.cell * 1.3);
      this.layers.low.add(d.spark);
    } else if (m.charge === 0 && d.spark) {
      d.spark.destroy(); d.spark = undefined;
    }
  }

  private dropSurface(k: string): void {
    const d = this.surfaces.get(k);
    if (!d) return;
    d.img.destroy(); d.glow?.destroy(); d.spark?.destroy();
    this.surfaces.delete(k);
  }

  // ------------------------------------------------------------------------------------------
  // clouds
  // ------------------------------------------------------------------------------------------

  private addCloud(k: string, t: Tile, cloud: CloudName): void {
    const art = CLOUD_ART[cloud];
    const c = this.centre(t);
    const puffs: CloudPuff[] = [];
    for (let i = 0; i < 2; i++) {
      const h = hash(t, 11 + i);
      const dx = ((h % 100) / 100 - 0.5) * this.cell * 0.4, dy = (((h >> 8) % 100) / 100 - 0.5) * this.cell * 0.4;
      const a0 = art.alpha * (i ? 0.8 : 1);
      const p = this.scene.add.image(c.x + dx, c.y + dy, MTEX.puff).setTint(art.tint).setAlpha(a0);
      p.setDisplaySize(this.cell * (1.35 + i * 0.2), this.cell * (1.2 + i * 0.15));
      this.layers.high.add(p);
      // the drift runs from `tick` rather than a looping tween: the scene kills every tween when a
      // new board arrives, and a cloud that stays across boards keeps its puffs
      puffs.push({ img: p, x: p.x, y: p.y, dx: (i ? -1 : 1) * this.cell * 0.18, dy: -this.cell * 0.08, a0, a1: art.alpha * (i ? 0.55 : 0.8), period: 2 * (1800 + (h % 900)) });
    }
    this.clouds.set(k, { puffs, cloud });
  }

  private dropCloud(k: string): void {
    const d = this.clouds.get(k);
    if (!d) return;
    for (const p of d.puffs) p.img.destroy();
    this.clouds.delete(k);
  }

  // ------------------------------------------------------------------------------------------
  // things
  // ------------------------------------------------------------------------------------------

  private has(atlas: string, frame: string): boolean {
    return this.scene.textures.exists(atlas) && this.scene.textures.get(atlas).has(frame);
  }

  /** Where a frame lives: an atlas frame, or a texture of its own (a drawn thing). */
  private source(frame: string): { tex: string; frame?: string } | undefined {
    if (frame.startsWith('item_')) return this.has('items', frame) ? { tex: 'items', frame } : undefined;
    if (frame.startsWith('world_')) return this.has('world', frame) ? { tex: 'world', frame } : undefined;
    return this.scene.textures.exists(frame) ? { tex: frame } : undefined;
  }

  private addThing(t: ThingView): void {
    if (this.addArt(t)) return;
    const look = THING_LOOK[t.kind];
    const objs: Phaser.GameObjects.GameObject[] = [];
    const c = this.centre(t.tile);
    const y = look.hangs ? c.y - this.cell * 0.22 : c.y;
    const lightsUp = LIGHTS.includes(t.kind);
    const frame = !t.lit && lightsUp && look.dark ? look.dark : look.frame;
    const src = this.source(frame) ?? (FALLBACK[t.kind] ? this.source(FALLBACK[t.kind]!) : undefined) ?? this.source(this.fallbackFrame());
    const d: DrawnThing = { view: t, objs };
    if (src) {
      const img = this.scene.add.image(c.x, y, src.tex, src.frame);
      const scale = (this.cell * (look.scale ?? 1)) / Math.max(1, img.width);
      img.setScale(scale);
      if (look.tint !== undefined) img.setTint(look.tint);
      // an unlit light with no dark frame of its own is drawn cold and grey
      if (lightsUp && !t.lit && !look.dark) img.setTint(0x6a6a70);
      this.layers.props.add(img);
      objs.push(img);
      d.img = img;
      d.atlas = src.tex;
      const animates = look.frameB && ((lightsUp && t.lit) || ALWAYS_ANIMATED.includes(t.kind)) && this.source(look.frameB);
      if (animates && src.frame) d.frames = [src.frame, look.frameB!];
    }
    if (look.mark && this.scene.textures.exists(markKey(look.mark))) {
      const m = this.scene.add.image(c.x, y, markKey(look.mark));
      m.setScale((this.cell * (look.scale ?? 1)) / ART);
      this.layers.props.add(m);
      objs.push(m);
    }
    if (look.hangs) {
      // the chain or rope it hangs by, up out of its square
      const g = this.scene.add.rectangle(c.x, t.tile.y * this.cell - this.cell * 0.15, Math.max(1, this.cell / 24 * 2), this.cell * 0.5, 0x3a3020).setOrigin(0.5, 0);
      this.layers.props.add(g);
      objs.push(g);
    }
    const burning = t.lit && (t.burn > 0 || (!lightsUp && t.kind !== 'HotOilVat'));
    if ((t.lit && lightsUp) || burning) {
      const glow = this.scene.add.image(c.x, y - this.cell * 0.1, MTEX.glow).setTint(burning ? 0xff6a2a : (look.light?.color ?? 0xffa040)).setBlendMode(Phaser.BlendModes.ADD).setAlpha(burning ? 0.6 : 0.35);
      glow.setDisplaySize(this.cell * (burning ? 1.8 : 1.2), this.cell * (burning ? 1.8 : 1.2));
      this.layers.props.add(glow);
      objs.push(glow);
      d.glow = glow;
    }
    this.things.set(t.id, d);
  }

  /**
   * docs/189 EO: a thing with its own art. Standing things and wall things go among the units at their feet's depth
   * (docs/186 EA3) and are lit and fogged by hand (`shade`); flat things lie in `low`; hanging things hang in `high`.
   * A lamp lit flickers between its frame and `_b`, put out it shows `_dark`; a thing on fire chars as it burns.
   */
  private addArt(t: ThingView): boolean {
    const v = objectVariant(objectTable(this.scene), t.kind, t.id);
    if (!v) return false;
    const shape = thingShape(t.kind);
    const lightsUp = LIGHTS.includes(t.kind);
    const frame = lightsUp && !t.lit && v.dark ? v.dark : v.frame;
    const tex = this.scene.textures.get(OBJECT_ATLAS.key);
    if (!tex.has(frame)) return false;
    const k = this.cell / 24;
    const a = anchorPx(t.tile, shape, this.cell);
    const objs: Phaser.GameObjects.GameObject[] = [];
    let img: Phaser.GameObjects.Image;
    const d: DrawnThing = { view: t, objs, atlas: OBJECT_ATLAS.key };
    if (shape.hangs) {
      const c = this.centre(t.tile);
      img = this.scene.add.image(c.x, c.y - this.cell * 0.22, OBJECT_ATLAS.key, frame).setScale(k);
      this.layers.high.add(img);
    } else if (shape.flat) {
      img = this.scene.add.image(a.x, (t.tile.y + shape.h / 2) * this.cell, OBJECT_ATLAS.key, frame).setScale(k);
      this.layers.low.add(img);
    } else {
      img = this.scene.add.image(a.x, a.y, OBJECT_ATLAS.key, frame).setOrigin(a.origin[0], a.origin[1]).setScale(k);
      if (this.hooks) {
        img.setDepth(standDepth(t.tile, shape, this.cell));
        this.hooks.units.add(img);
        d.among = true;
      } else this.layers.props.add(img);
    }
    // an unlit lamp with no dark drawing of its own is drawn cold and grey
    if (lightsUp && !t.lit && !v.dark) img.setTint(0x6a6a70);
    objs.push(img);
    d.img = img;
    if (lightsUp && t.lit && v.b && tex.has(v.b)) d.frames = [v.frame, v.b];
    const burning = t.lit && t.burn > 0;
    if ((t.lit && lightsUp) || burning) {
      const gx = a.x, gy = shape.hangs ? this.centre(t.tile).y - this.cell * 0.3 : (t.tile.y + shape.h) * this.cell - this.cell * 0.6;
      const size = this.cell * (burning ? 1.4 + 0.4 * Math.max(shape.w, shape.h) : 1.2);
      const glow = this.scene.add.image(gx, gy, MTEX.glow).setTint(burning ? 0xff6a2a : (THING_LOOK[t.kind].light?.color ?? 0xffa040)).setBlendMode(Phaser.BlendModes.ADD).setAlpha(burning ? 0.6 : 0.35);
      glow.setDisplaySize(size, size);
      if (d.among) { glow.setDepth(img.depth + 0.001); this.hooks!.units.add(glow); } else this.layers.props.add(glow);
      objs.push(glow);
      d.glow = glow;
    }
    this.things.set(t.id, d);
    this.shade(d);
    return true;
  }

  /** The char a burning thing takes on, deeper as its fire runs down (1: none). */
  private char(t: ThingView): number {
    if (!(t.lit && t.burn > 0) || LIGHTS.includes(t.kind)) return 1;
    return Math.max(0.35, 0.45 + 0.15 * Math.min(3, t.burn));
  }

  /** docs/189: a thing among the units takes the light map at its corners and the fog of its squares, as the terrain's standing images do. */
  private shade(d: DrawnThing): void {
    const img = d.img;
    if (!img) return;
    const h = this.hooks, t = d.view;
    const char = this.char(t);
    if (!d.among || !h) {
      if (char < 1) img.setTint(hexOf(char, char * 0.92, char * 0.85));
      else if (img.isTinted && !(LIGHTS.includes(t.kind) && !t.lit)) img.clearTint();
      return;
    }
    const sq = footprint(t.tile, thingShape(t.kind));
    const seen = Math.max(...sq.map((q) => h.fog?.(q.x, q.y) ?? 2));
    const vis = seen > 0;
    for (const o of d.objs) (o as unknown as { setVisible(v: boolean): unknown }).setVisible(vis && !this.hidden.has(t.id));
    if (!vis) return;
    const mem = seen === 1 ? h.memory?.() ?? [0.55, 0.55, 0.6] : undefined;
    if (mem && d.glow) d.glow.setVisible(false);
    const c = this.cell;
    const b = img.getBounds();
    const at = (x: number, y: number) => h.light?.(x / c, y / c);
    const corners = [at(b.left + 1, b.top + 1), at(b.right - 1, b.top + 1), at(b.left + 1, b.bottom - 1), at(b.right - 1, b.bottom - 1)];
    if (!corners[0] && !mem && char === 1) { if (img.isTinted) img.clearTint(); return; }
    const ch = char < 1 ? [char, char * 0.92, char * 0.85] : [1, 1, 1];
    const k = corners.map((rgb) => hexOf((rgb?.[0] ?? 1) * (mem?.[0] ?? 1) * ch[0]!, (rgb?.[1] ?? 1) * (mem?.[1] ?? 1) * ch[1]!, (rgb?.[2] ?? 1) * (mem?.[2] ?? 1) * ch[2]!));
    img.setTint(k[0]!, k[1]!, k[2]!, k[3]!);
  }

  private fallbackFrame(): string { return 'world_0268'; }

  private dropThing(id: number): void {
    const d = this.things.get(id);
    if (!d) return;
    for (const o of d.objs) o.destroy();
    this.things.delete(id);
  }

  /** How many things are drawn, and how many from the objects atlas (review). */
  drawnCount(): { all: number; art: number } {
    let art = 0;
    for (const d of this.things.values()) if (d.atlas === OBJECT_ATLAS.key) art++;
    return { all: this.things.size, art };
  }

  /** Re-light and re-fog every thing drawn among the units (the Encounter calls this when the fog changes). */
  shadeAll(): void {
    for (const d of this.things.values()) if (d.among || d.view.burn > 0) this.shade(d);
  }

  /** Flicker between frames: braziers, fires, cauldrons, candles. */
  private flip(): void {
    this.frameFlip = !this.frameFlip;
    for (const d of this.things.values()) {
      if (!d.frames || !d.img || !d.atlas) continue;
      d.img.setFrame(d.frames[this.frameFlip ? 1 : 0]);
    }
  }

  // ------------------------------------------------------------------------------------------
  // the shared emitters
  // ------------------------------------------------------------------------------------------

  private zone(squares: () => Tile[], inset: number): Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig {
    const cell = this.cell;
    return {
      type: 'random',
      source: {
        getRandomPoint: (p: Phaser.Types.Math.Vector2Like) => {
          const list = squares();
          const s = list[Math.floor(Math.random() * list.length)];
          if (!s) { p.x = -9999; p.y = -9999; return; }
          p.x = (s.x + inset + Math.random() * (1 - 2 * inset)) * cell;
          p.y = (s.y + inset + Math.random() * (1 - 2 * inset)) * cell;
        },
      },
    };
  }

  private emitter(layer: Layer, tex: string, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig): Emitter {
    const e = this.scene.add.particles(0, 0, tex, cfg);
    layer.add(e);
    return e;
  }

  /** Point each shared emitter at the squares that need it now; start or stop it; set its rate. */
  private rezone(): void {
    const z = this.zones;
    z.fire = []; z.tall = []; z.fizz = []; z.ember = []; z.charge = []; z.motes = new Map();
    for (const m of this.patches) {
      if (m.fire > 0) { z.fire.push(m.tile); if (m.surface && TALL_FLAME.has(m.surface)) z.tall.push(m.tile); }
      else if (m.surface && FIZZ.has(m.surface)) z.fizz.push(m.tile);
      else if (m.surface === 'Embers') z.ember.push(m.tile);
      if (m.charge > 0) z.charge.push(m.tile);
      if (m.cloud && CLOUD_ART[m.cloud].motes) {
        const l = z.motes.get(m.cloud) ?? [];
        l.push(m.tile);
        z.motes.set(m.cloud, l);
      }
    }
    // things on fire burn like burning ground
    for (const d of this.things.values()) if (d.view.lit && d.view.burn > 0) for (const q of footprint(d.view.tile, thingShape(d.view.kind))) { z.fire.push(q); z.tall.push(q); }
    if (this.still) return;
    const cell = this.cell;
    const k = cell / 24;
    const rate = (n: number, per: number) => 1000 / Math.max(0.5, n * per);
    // flames
    if (z.fire.length) {
      this.emitters.fire ??= this.emitter(this.layers.effects, MTEX.flame, {
        emitZone: this.zone(() => (Math.random() < 0.4 && this.zones.tall.length ? this.zones.tall : this.zones.fire), 0.15),
        lifespan: { min: 380, max: 720 }, speedY: { min: -26 * k, max: -12 * k }, speedX: { min: -4 * k, max: 4 * k },
        scale: { start: 1.1 * k, end: 0.3 * k }, tint: [0xffe08a, 0xffa040, 0xff6a2a, 0xd83a1a], blendMode: Phaser.BlendModes.ADD,
        alpha: { start: 0.95, end: 0 }, frequency: 100,
      });
      this.emitters.fire.setFrequency(rate(z.fire.length, 9));
      this.emitters.fire.start();
    } else this.emitters.fire?.stop();
    // bubbles and a faint wisp off acid, venom, blight and slime
    if (z.fizz.length) {
      this.emitters.fizz ??= this.emitter(this.layers.low, MTEX.bubble, {
        emitZone: this.zone(() => this.zones.fizz, 0.2), lifespan: { min: 500, max: 900 }, speedY: { min: -6 * k, max: -2 * k },
        scale: { start: 0.6 * k, end: 1.1 * k }, tint: [0xd2ff7a, 0xf0fff0, 0xc8a0ff], alpha: { start: 0.9, end: 0 }, frequency: 200,
      });
      this.emitters.wisp ??= this.emitter(this.layers.effects, MTEX.puff, {
        emitZone: this.zone(() => this.zones.fizz, 0.25), lifespan: { min: 1400, max: 2200 }, speedY: { min: -10 * k, max: -5 * k }, speedX: { min: -3 * k, max: 3 * k },
        scale: { start: 0.12 * k, end: 0.4 * k }, tint: [0xc8e8a0, 0xd8d0e8], alpha: { onEmit: () => 0, onUpdate: (_p, _k, t) => 0.22 * Math.sin(t * Math.PI) }, frequency: 400,
      });
      this.emitters.fizz.setFrequency(rate(z.fizz.length, 2.5)); this.emitters.fizz.start();
      this.emitters.wisp.setFrequency(rate(z.fizz.length, 0.7)); this.emitters.wisp.start();
    } else { this.emitters.fizz?.stop(); this.emitters.wisp?.stop(); }
    // embers spit sparks
    if (z.ember.length) {
      this.emitters.ember ??= this.emitter(this.layers.effects, MTEX.dot, {
        emitZone: this.zone(() => this.zones.ember, 0.2), lifespan: { min: 400, max: 800 }, speedY: { min: -22 * k, max: -10 * k }, speedX: { min: -6 * k, max: 6 * k },
        gravityY: 20 * k, scale: k, tint: [0xffe08a, 0xffa040, 0xff6a2a], blendMode: Phaser.BlendModes.ADD, alpha: { start: 1, end: 0 }, frequency: 300,
      });
      this.emitters.ember.setFrequency(rate(z.ember.length, 1.2)); this.emitters.ember.start();
    } else this.emitters.ember?.stop();
    // crackling sparks on charged squares
    if (z.charge.length) {
      this.emitters.charge ??= this.emitter(this.layers.effects, MTEX.spark, {
        emitZone: this.zone(() => this.zones.charge, 0.1), lifespan: { min: 90, max: 220 }, speed: { min: 10 * k, max: 40 * k },
        scale: { start: 0.9 * k, end: 0.2 * k }, tint: [0xffffff, 0xbfe8ff, 0x7ac8ff], blendMode: Phaser.BlendModes.ADD, alpha: { start: 1, end: 0 }, frequency: 60,
      });
      this.emitters.charge.setFrequency(rate(z.charge.length, 10)); this.emitters.charge.start();
    } else this.emitters.charge?.stop();
    // the motes of a swarm, pollen, spores, cinders, frost, incense, ash
    for (const cloud of Object.keys(CLOUD_ART) as CloudName[]) {
      const motes = CLOUD_ART[cloud].motes;
      if (!motes) continue;
      const tiles = z.motes.get(cloud);
      let e = this.emitters.motes.get(cloud);
      if (!tiles?.length) { e?.stop(); continue; }
      const swarm = cloud === 'Swarm';
      if (!e) {
        e = this.emitter(this.layers.high, motes.tex, {
          emitZone: this.zone(() => this.zones.motes.get(cloud) ?? [], 0.05),
          lifespan: swarm ? { min: 500, max: 900 } : { min: 1200, max: 2200 },
          speed: swarm ? { min: 18 * k, max: 40 * k } : { min: 2 * k, max: 8 * k },
          speedY: cloud === 'Cinders' ? { min: -16 * k, max: -6 * k } : undefined,
          scale: k * (swarm ? 0.9 : 1), tint: motes.tint, alpha: { onEmit: () => 0, onUpdate: (_p, _k, t) => Math.sin(t * Math.PI) },
          blendMode: cloud === 'Cinders' || cloud === 'Frost' || cloud === 'Incense' ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL,
          frequency: 100,
        });
        this.emitters.motes.set(cloud, e);
      }
      e.setFrequency(rate(tiles.length, swarm ? 22 : 3));
      e.start();
    }
  }

  /** Liquids shimmer, glows flicker, charges flash. */
  private tick(time: number): void {
    const t = time / 1000;
    for (const d of this.surfaces.values()) {
      if (LIQUID.has(d.surface)) d.img.setAlpha(0.88 + 0.12 * Math.sin(t * 1.6 + d.phase));
      if (d.glow) d.glow.setAlpha(0.45 + 0.2 * Math.sin(t * 9 + d.phase) + 0.08 * Math.sin(t * 23 + d.phase * 2));
      if (d.spark) d.spark.setAlpha(Math.random() < 0.25 ? 0.95 : 0.25 + 0.2 * Math.random());
    }
    // clouds drift out and back (a sine in and out, as the yoyo it replaces)
    for (const d of this.clouds.values()) for (const p of d.puffs) {
      p.t0 ??= time;
      const k = 0.5 - 0.5 * Math.cos(((time - p.t0) / p.period) * Math.PI * 2);
      p.img.setPosition(p.x + p.dx * k, p.y + p.dy * k).setAlpha(p.a0 + (p.a1 - p.a0) * k);
    }
    for (const d of this.things.values()) if (d.glow) d.glow.setAlpha(0.32 + 0.12 * Math.sin(t * 8 + d.view.id) + 0.06 * Math.sin(t * 19 + d.view.id));
    // docs/189: things among the units follow the light map and the fog, four times a second
    if (time - this.shadeAt > 250) { this.shadeAt = time; this.shadeAll(); }
  }

  // ------------------------------------------------------------------------------------------
  // a reaction firing
  // ------------------------------------------------------------------------------------------

  react(look: ReactLook, at: Tile, tiles: readonly Tile[], ms: number): void {
    if (this.destroyed) return;
    const cell = this.cell, k = cell / 24;
    const c = this.centre(at);
    const span = Math.max(200, ms);
    const squares = tiles.length ? tiles : [at];
    const flash = (p: { x: number; y: number }, tint: number, size: number, life = span * 0.6) => {
      const g = this.own(this.scene.add.image(p.x, p.y, MTEX.glow).setTint(tint).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.9));
      g.setDisplaySize(cell * size, cell * size);
      this.scene.tweens.add({ targets: g, alpha: 0, scale: g.scale * 1.4, duration: life, onComplete: () => this.drop(g) });
    };
    const burst = (p: { x: number; y: number }, tex: string, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig, n: number) => {
      const e = this.own(this.scene.add.particles(p.x, p.y, tex, { emitting: false, ...cfg }));
      e.explode(n, 0, 0);
      this.scene.time.delayedCall(span + 900, () => this.drop(e));
    };
    const tint: Partial<Record<ReactLook, number>> = {
      blast: 0xff8a30, shock: 0xbfe8ff, ignite: 0xffa040, douse: 0xe8eef4, cloud: 0xd8d8d8, freeze: 0xcff0ff, melt: 0x9fd2f2,
      splash: 0x9fd2f2, break: 0xc9a06a, tip: 0xb8a080, roll: 0xa89880, fall: 0xb8a890, toll: 0xffe08a, collapse: 0x9a9088,
      grow: 0x8ad86a, gust: 0xeef4ff, ground: 0x9a8a70, glow: 0xffffd0, sting: 0xff5a5a, plain: 0xffffff,
    };
    const col = tint[look] ?? 0xffffff;
    if (this.still) { flash(c, col, 1.4, 220); return; }
    switch (look) {
      case 'blast': {
        flash(c, 0xffd080, 3.2);
        flash(c, 0xff6a2a, 2.2, span);
        burst(c, MTEX.flame, { lifespan: { min: 300, max: 650 }, speed: { min: 40 * k, max: 110 * k }, scale: { start: 1.4 * k, end: 0.2 * k }, tint: [0xffe08a, 0xffa040, 0xff5a1a], blendMode: Phaser.BlendModes.ADD, alpha: { start: 1, end: 0 } }, 26);
        burst(c, MTEX.bit, { lifespan: { min: 400, max: 800 }, speed: { min: 50 * k, max: 120 * k }, gravityY: 160 * k, scale: k, tint: [0x5a3a1e, 0x3a3a3a, 0x8a5a30], rotate: { min: 0, max: 360 } }, 12);
        burst(c, MTEX.puff, { lifespan: { min: 900, max: 1500 }, speed: { min: 6 * k, max: 22 * k }, speedY: { min: -18 * k, max: -6 * k }, scale: { start: 0.3 * k, end: 0.9 * k }, tint: 0x3a3a3e, alpha: { start: 0.55, end: 0 } }, 6);
        if (this.scene.cameras.main) this.scene.cameras.main.shake(Math.min(260, span * 0.5), 0.004);
        break;
      }
      case 'shock': {
        const pts = squares.slice(0, 24).map((t) => this.centre(t));
        const g = this.own(this.scene.add.graphics());
        const draw = () => {
          g.clear();
          g.lineStyle(Math.max(1, 2 * k), 0xeaf6ff, 1);
          for (let i = 1; i < pts.length; i++) {
            const a = pts[i - 1]!, b = pts[i]!;
            g.beginPath(); g.moveTo(a.x, a.y);
            for (let s = 1; s < 4; s++) g.lineTo(a.x + ((b.x - a.x) * s) / 4 + (Math.random() - 0.5) * cell * 0.4, a.y + ((b.y - a.y) * s) / 4 + (Math.random() - 0.5) * cell * 0.4);
            g.lineTo(b.x, b.y); g.strokePath();
          }
        };
        draw();
        this.scene.time.addEvent({ delay: 60, repeat: Math.max(1, Math.floor((span * 0.6) / 60)), callback: draw });
        this.scene.time.delayedCall(span * 0.7, () => this.drop(g));
        for (const p of pts) flash(p, 0xbfe8ff, 1.3, span * 0.5);
        break;
      }
      case 'ignite':
        for (const t of squares.slice(0, 16)) {
          const p = this.centre(t);
          flash(p, 0xffa040, 1.5, span * 0.7);
          burst(p, MTEX.flame, { lifespan: { min: 300, max: 600 }, speedY: { min: -50 * k, max: -20 * k }, speedX: { min: -12 * k, max: 12 * k }, scale: { start: 1.2 * k, end: 0.2 * k }, tint: [0xffe08a, 0xffa040, 0xff5a1a], blendMode: Phaser.BlendModes.ADD, alpha: { start: 1, end: 0 } }, 8);
        }
        break;
      case 'douse': case 'cloud': case 'melt': {
        const steam = look === 'douse' ? 0xf2f4f6 : look === 'melt' ? 0x9fd2f2 : 0xd8d8d8;
        for (const t of squares.slice(0, 12)) burst(this.centre(t), MTEX.puff, { lifespan: { min: 700, max: 1200 }, speed: { min: 8 * k, max: 24 * k }, speedY: { min: -20 * k, max: -6 * k }, scale: { start: 0.25 * k, end: 0.8 * k }, tint: steam, alpha: { start: 0.6, end: 0 } }, 4);
        if (look === 'melt') for (const t of squares.slice(0, 12)) burst(this.centre(t), MTEX.dot, { lifespan: 500, speedY: { min: 10 * k, max: 30 * k }, gravityY: 60 * k, scale: k, tint: 0x9fd2f2 }, 5);
        break;
      }
      case 'freeze':
        for (const t of squares.slice(0, 16)) {
          const p = this.centre(t);
          flash(p, 0xcff0ff, 1.6, span * 0.8);
          burst(p, MTEX.spark, { lifespan: { min: 300, max: 700 }, speed: { min: 10 * k, max: 40 * k }, scale: { start: 1.1 * k, end: 0.2 * k }, tint: [0xffffff, 0xcff0ff], blendMode: Phaser.BlendModes.ADD, alpha: { start: 1, end: 0 } }, 6);
        }
        break;
      case 'splash': case 'tip':
        for (const t of squares.slice(0, 12)) burst(this.centre(t), look === 'tip' ? MTEX.bit : MTEX.dot, { lifespan: { min: 300, max: 600 }, speed: { min: 20 * k, max: 60 * k }, gravityY: 120 * k, scale: k, tint: [0x9fd2f2, 0xc8c0b0, 0x8a6a4a] }, 6);
        if (look === 'tip') burst(c, MTEX.puff, { lifespan: 700, speed: { min: 6 * k, max: 18 * k }, scale: { start: 0.2 * k, end: 0.6 * k }, tint: 0xb8a080, alpha: { start: 0.5, end: 0 } }, 4);
        break;
      case 'break': case 'collapse': case 'fall': case 'ground': case 'roll': {
        const n = look === 'collapse' ? 18 : 10;
        const debris = look === 'break' ? [0x8a5a30, 0x5a3a1e, 0xc9a06a] : [0x6e6a64, 0x9c968e, 0x4a4640];
        for (const t of squares.slice(0, look === 'roll' ? 12 : 6)) {
          const p = this.centre(t);
          burst(p, MTEX.bit, { lifespan: { min: 350, max: 700 }, speed: { min: 30 * k, max: 90 * k }, gravityY: 180 * k, scale: k, tint: debris, rotate: { min: 0, max: 360 } }, look === 'roll' ? 3 : n);
          burst(p, MTEX.puff, { lifespan: { min: 700, max: 1300 }, speed: { min: 6 * k, max: 20 * k }, scale: { start: 0.25 * k, end: 0.8 * k }, tint: 0xb8b0a0, alpha: { start: 0.55, end: 0 } }, look === 'collapse' ? 5 : 2);
        }
        if (look === 'collapse' || look === 'fall') this.scene.cameras.main?.shake(Math.min(220, span * 0.4), 0.003);
        break;
      }
      case 'toll': {
        for (let i = 0; i < 3; i++) {
          const r = this.own(this.scene.add.image(c.x, c.y, MTEX.ring).setTint(0xffe08a).setBlendMode(Phaser.BlendModes.ADD).setAlpha(0.9));
          r.setDisplaySize(cell * 0.6, cell * 0.6);
          this.scene.tweens.add({ targets: r, displayWidth: cell * 6, displayHeight: cell * 6, alpha: 0, delay: i * 160, duration: span, onComplete: () => this.drop(r) });
        }
        break;
      }
      case 'grow':
        for (const t of squares.slice(0, 12)) burst(this.centre(t), MTEX.bit, { lifespan: 600, speed: { min: 15 * k, max: 45 * k }, scale: k, tint: [0x5ab83a, 0x8ad86a, 0x3a8a2a], rotate: { min: 0, max: 360 } }, 6);
        break;
      case 'gust':
        for (const t of squares.slice(0, 12)) burst(this.centre(t), MTEX.bolt, { lifespan: 450, speedX: { min: 60 * k, max: 120 * k }, speedY: { min: -6 * k, max: 6 * k }, scale: k, tint: 0xeef4ff, alpha: { start: 0.7, end: 0 } }, 3);
        break;
      case 'glow':
        flash(c, 0xffffd0, 2, span);
        burst(c, MTEX.spark, { lifespan: 700, speed: { min: 10 * k, max: 30 * k }, scale: { start: k, end: 0 }, tint: [0xffffff, 0xffe08a], blendMode: Phaser.BlendModes.ADD }, 10);
        break;
      case 'sting':
        flash(c, 0xff5a5a, 1.2, 260);
        break;
      default:
        burst(c, MTEX.spark, { lifespan: 400, speed: { min: 10 * k, max: 25 * k }, scale: { start: k, end: 0 }, tint: col, blendMode: Phaser.BlendModes.ADD }, 5);
        break;
    }
  }

  private own<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.layers.effects.add(o);
    this.oneShots.add(o);
    // whatever happens to its tweens, a flourish is gone within a few seconds
    this.scene.time.delayedCall(4000, () => this.drop(o));
    return o;
  }

  private drop(o: Phaser.GameObjects.GameObject): void {
    if (!this.oneShots.delete(o)) return;
    o.destroy();
  }

  destroy(): void {
    this.destroyed = true;
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    this.flipTimer?.remove();
    for (const k of [...this.surfaces.keys()]) this.dropSurface(k);
    for (const k of [...this.clouds.keys()]) this.dropCloud(k);
    for (const id of [...this.things.keys()]) this.dropThing(id);
    for (const e of [this.emitters.fire, this.emitters.fizz, this.emitters.wisp, this.emitters.ember, this.emitters.charge, ...this.emitters.motes.values()]) e?.destroy();
    this.emitters = { motes: new Map() };
    for (const o of this.oneShots) o.destroy();
    this.oneShots.clear();
  }
}
